import Modal from "@components/Modal";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { downloadCsv } from "@utils/downloadCsv";
import { formatDateDMY } from "@utils/formatDate";
import { formatNumberToCurrency } from "@utils/formatNumber";
import { findWbsPath } from "@utils/formatWbsPath";
import { isPresentationProject } from "@utils/presentationMode";
import { isUpdateJobActive, updateJobMessage } from "@contexts/projects/updateJobs";
import { useEffect, useRef, useState } from "react";
import Select from 'react-select';
import AnnulmentIndicator from "@components/titleAnnulments/AnnulmentIndicator";
import { UNAPPROPRIATED_ORDERS_CONTRACTS_LABEL, isUnappropriatedOrdersContractsNode } from "@utils/forcedStructures";

const renderRow = (label, value) => {
  return <div className="data">
    <span className="accordion-label">{label}</span>
    <span className="value">{value}</span>
  </div>
}

const getStatus = {
  PENDING: 'Pendente',
  FULLY_DELIVERED: 'Entregue',
  PARTIALLY_DELIVERED: 'Entregue parcialmente',
}

// Valor ausente (null/undefined/vazio/NaN) vira "—": `formatNumberToCurrency` sozinho devolve
// "R$ NaN" para undefined e quebra com string, e o documento MEGA nem sempre traz todos os campos.
const MISSING_VALUE = '—';

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatCurrencyOrDash = (value) => {
  const parsed = toFiniteNumber(value);
  return parsed === null ? MISSING_VALUE : formatNumberToCurrency(parsed);
};

const textOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' || text === 'undefined' || text === 'null' ? null : text;
};

const joinText = (parts, separator = ' - ') => parts.map(textOrNull).filter(Boolean).join(separator);

// "AAAA-MM-DD" (com ou sem hora ISO) -> "DD/MM/AAAA", sem passar por fuso.
export const formatContractDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : MISSING_VALUE;
};

// Segmento próprio do fornecedor, em largura total entre o título e a grade de valores: o
// nome do fornecedor é o que identifica o pedido/contrato para quem opera suprimentos, e
// espremido no título (ou num tooltip) ele se perdia. O código do ERP vai junto, em segundo
// plano; sem nome, o código ocupa o lugar dele.
export const SupplierSegment = ({ name, code }) => {
  const supplierName = textOrNull(name);
  const supplierCode = textOrNull(code);
  return <div className="supplier-segment margin-top-8" data-testid="supplier-segment">
    <span className="accordion-label">Fornecedor</span>
    <span className="value supplier-name">{supplierName ?? (supplierCode ? `Código ${supplierCode}` : MISSING_VALUE)}</span>
    {supplierName && supplierCode && <span className="supplier-code">Código {supplierCode}</span>}
  </div>
};

const hasField = (record, field) => record?.[field] !== undefined && record?.[field] !== null;

export const purchaseOrderWbsLabels = (item = {}) => {
  const legacy = String(item.wbs_code || '').split('.');
  const suffix = (value, length, fallback = '') => {
    const normalized = String(value || '');
    return normalized ? normalized.slice(-length) : fallback;
  };

  return {
    unit: suffix(item.wbs_code_id_unit, 3),
    cell: suffix(item.wbs_code_id_cell, 2, legacy[0] || ''),
    step: suffix(item.wbs_code_id_step, 3, legacy[1] || ''),
    substep: suffix(item.wbs_code_id_substep, 3, legacy[2] || ''),
    service: suffix(item.wbs_code_id_service, 3, legacy[3] || ''),
  };
};

const Item = ({ index, resource, paid }) => {
  const [open, setOpen] = useState(false)

  const getLabel = (label) => {
    switch (label) {
      case 'unit':
        return 'Unidade'
      case 'cell':
        return 'Célula'
      case 'step':
        return 'Etapa'
      case 'substep':
        return 'Subetapa'
      case 'service':
        return 'Serviço'
      default:
        break;
    }
  }

  return <div key={index} className={`accordion-item offcanvas-accordion-item curve margin-bottom-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title" title={resource.supplier_name}>{resource.contract_number} - {resource.supplier_name}</span>
      </div>
    </div>
    <div className="grid gap-16 columns-4-laptop-l margin-top-12">
      {renderRow(`Documento`, resource.document_id)}
      {renderRow('Status', resource.status)}
      {renderRow(`Data do contrato`, resource?.contract_date?.split('-').reverse().join('/'))}
      {renderRow('Valor', formatNumberToCurrency(resource.total_labor_value))}
    </div>
    {open && <div className="grid gap-16 columns-4 margin-top-12">
      {renderRow(`Tipo de contrato`, resource.contract_type)}
      {renderRow(`Descrição`, resource.description)}
      {renderRow(`Descrição completa`, resource.description_full)}
      {renderRow(`Responsável`, resource.responsible_name)}
      {renderRow(`Preço do material`, resource.material_price)}
      {renderRow(`Quantidade medida`, resource.measured_quantity)}
      {renderRow(`Valor medido`, resource.measured_value)}
      {renderRow(`Quantidade`, `${resource.quantity}${resource.unit_of_measure}`)}
      {renderRow(`Fornecedor`, resource.supplier_name)}
      {renderRow(`Id do fornecedor`, resource.supplier_id)}

    </div>}
    {open && <div className={`grid gap-16 columns-1-laptop-l margin-top-12`}>
      {Object.entries(resource.wbs_info).map(([label, value]) => {
        if (value === undefined) return null
        return renderRow(getLabel(label), value)
      })}
    </div>}
  </div>
}

// Valores do contrato agregado (e de cada item) -- mesma ordem no cartão e no expandido.
const contractValueRows = (record) => <>
  {renderRow('Contratado', formatCurrencyOrDash(record?.total_contratado))}
  {renderRow('Distratado', formatCurrencyOrDash(record?.total_distratado))}
  {renderRow('Medido', formatCurrencyOrDash(record?.medicao))}
  {renderRow('Faturado (NF)', formatCurrencyOrDash(record?.invoiced_value))}
  {renderRow('Saldo (ERP)', formatCurrencyOrDash(record?.saldo))}
  {renderRow('Saldo a faturar', formatCurrencyOrDash(record?.balance_to_invoice))}
</>

// Vínculo de UMA linha de contrato MEGA com o orçamento. É por linha, nunca por contrato: o mesmo
// contrato pode ter itens em serviços diferentes. Três estados distintos (`budget_link_status`):
// `linked` mostra o caminho da EAP e o serviço; `unlinked` é "a rota foi consultada e não devolveu
// a linha"; `unknown` é "não foi possível afirmar". Linha gravada antes do vínculo (sem o campo)
// não mostra nada, como era.
export const MEGA_BUDGET_LINK_LABELS = {
  unlinked: 'Sem vínculo com o orçamento',
  unknown: 'Vínculo com o orçamento desconhecido',
}

// `findWbsPath` devolve "-" quando o nível não está na EAP carregada.
const MISSING_PATH = '-'

// Motivo pelo qual o pipeline ancorou o pedido ou a linha de contrato na estrutura
// "PEDIDOS E CONTRATOS SEM APROPRIAÇÃO" (`budget_unlinked_reason`). Separa o que o ERP ainda não
// informou (pedido sem título), o que a fonte declarou (fora da rota) e o que não casou.
export const UNLINKED_REASON_LABELS = {
  no_title: 'Aguardando título',
  title_without_structured: 'Título sem estruturado',
  code_not_in_eap: 'Código sem serviço na EAP',
  titles_unavailable: 'Títulos indisponíveis na última carga',
  not_in_route: 'Fora da rota de contratos do ERP',
  route_failed: 'Falha na rota de contratos do ERP',
  budget_not_served: 'Orçamento sem contratos na rota do ERP',
  ambiguous_line: 'Linha ambígua na rota do ERP',
}

export const unlinkedReasonLabel = (reason) => UNLINKED_REASON_LABELS[reason] ?? textOrNull(reason) ?? MISSING_VALUE

const UnlinkedReasonRows = ({ reason }) => {
  if (!textOrNull(reason)) return null
  return <>
    {renderRow('Estrutura', UNAPPROPRIATED_ORDERS_CONTRACTS_LABEL)}
    <div className="data" data-testid="mega-unlinked-reason" data-reason={reason}>
      <span className="accordion-label">Motivo</span>
      <span className="value">{unlinkedReasonLabel(reason)}</span>
    </div>
  </>
}

// Resumo do nó da estrutura escolhido no filtro de nível: total da origem da aba (pedidos ou
// linhas de contrato) e o agrupamento por motivo, calculados pelo back-end em leitura.
export const UnlinkedSummary = ({ summary, origin }) => {
  const isOrders = origin === 'purchases'
  const groups = Array.isArray(summary?.by_reason) ? summary.by_reason : []
  const count = Number(summary?.count) || 0
  return <div className="accordion-item offcanvas-accordion-item curve margin-top-16" data-testid="unlinked-summary" data-origin={origin}>
    <span className="accordion-title">{UNAPPROPRIATED_ORDERS_CONTRACTS_LABEL}</span>
    {count === 0
      ? <p className="accordion-label margin-top-8">
        {isOrders ? 'Não há pedidos sem apropriação nesta obra.' : 'Não há contratos sem apropriação nesta obra.'}
      </p>
      : <>
        <div className="grid gap-16 columns-4-laptop-l margin-top-12">
          {renderRow(isOrders ? 'Pedidos' : 'Linhas de contrato', count)}
          {isOrders
            ? renderRow('Valor dos pedidos', formatCurrencyOrDash(summary?.totals?.value))
            : <>
              {renderRow('Valor contratado', formatCurrencyOrDash(summary?.totals?.total_contratado))}
              {renderRow('Saldo a faturar', formatCurrencyOrDash(summary?.totals?.balance_to_invoice))}
            </>}
        </div>
        <table className="margin-top-12" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Motivo</th>
              <th style={{ textAlign: 'right' }}>Quantidade</th>
              {isOrders
                ? <th style={{ textAlign: 'right' }}>Valor</th>
                : <>
                  <th style={{ textAlign: 'right' }}>Contratado</th>
                  <th style={{ textAlign: 'right' }}>Saldo a faturar</th>
                </>}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => <tr key={group.reason} data-testid="unlinked-summary-reason" data-reason={group.reason}>
              <td>{unlinkedReasonLabel(group.reason)}</td>
              <td style={{ textAlign: 'right' }}>{group.count}</td>
              {isOrders
                ? <td style={{ textAlign: 'right' }}>{formatCurrencyOrDash(group?.totals?.value)}</td>
                : <>
                  <td style={{ textAlign: 'right' }}>{formatCurrencyOrDash(group?.totals?.total_contratado)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrencyOrDash(group?.totals?.balance_to_invoice)}</td>
                </>}
            </tr>)}
          </tbody>
        </table>
      </>}
  </div>
}

const megaLineWbsPath = (appropriations, line) => [
  findWbsPath(appropriations, 'cells', line?.wbs_code_id_cell),
  findWbsPath(appropriations, 'steps', line?.wbs_code_id_step),
  findWbsPath(appropriations, 'substeps', line?.wbs_code_id_substep),
].filter((name) => name && name !== MISSING_PATH)


export const MegaBudgetLink = ({ line, appropriations }) => {
  const status = line?.budget_link_status
  if (status === 'linked') {
    const service = findWbsPath(appropriations, 'services', line?.wbs_code_id_service)
    const path = megaLineWbsPath(appropriations, line)
    const code = textOrNull(line?.budget_structured_code)
    return <div className="grid gap-16 columns-1 padding-8 border-top" data-testid="mega-budget-link" data-status="linked">
      {path.length > 0 && renderRow('EAP', path.join(' › '))}
      {renderRow('Serviço apropriado', joinText([code, service === MISSING_PATH ? null : service]) || MISSING_VALUE)}
    </div>
  }
  const label = MEGA_BUDGET_LINK_LABELS[status]
  if (!label) return null
  return <div className="grid gap-16 columns-1 padding-8 border-top" data-testid="mega-budget-link" data-status={status}>
    <span className="accordion-label">{label}</span>
    <UnlinkedReasonRows reason={line?.budget_unlinked_reason} />
  </div>
}

// Vínculo de um pedido MEGA com o orçamento, pelos títulos do ERP que citam o pedido. O pedido pode
// ter vários serviços (`budget_services`), então o cartão lista todos e nunca afirma um serviço
// único. `unknown` sem código não casado é "nenhum título cita o pedido ainda" -- não é ausência de
// vínculo, e por isso nunca aparece como "Sem vínculo". Pedido gravado antes do vínculo (sem o
// campo) não mostra nada, como era.
export const MEGA_ORDER_WITHOUT_TITLE = 'Nenhum título do ERP cita este pedido ainda.'
export const MEGA_ORDER_AWAITING_TITLE = 'Aguardando título'
export const MEGA_ITEM_LINKED_BY_ORDER = 'Vínculo no nível do pedido'

export const MegaOrderBudgetLink = ({ order, appropriations }) => {
  const status = order?.budget_link_status
  const services = Array.isArray(order?.budget_services) ? order.budget_services : []
  const unmatched = (Array.isArray(order?.budget_unmatched_codes) ? order.budget_unmatched_codes : [])
    .map(textOrNull).filter(Boolean)
  if (status === 'linked') {
    return <div data-testid="mega-order-budget-link" data-status="linked">
      {services.map((service, idx) => (
        <MegaBudgetLink
          key={service?.wbs_code_id_service ?? service?.budget_structured_code ?? idx}
          line={{ ...service, budget_link_status: 'linked' }}
          appropriations={appropriations}
        />
      ))}
      {unmatched.length > 0 && <div className="grid gap-16 columns-1 padding-8 border-top">
        {renderRow('Códigos sem serviço na EAP', unmatched.join(', '))}
      </div>}
    </div>
  }
  if (status !== 'unknown') return null
  const reason = textOrNull(order?.budget_unlinked_reason)
  // Sem título ainda não é "sem apropriação definitiva": o pedido aguarda o título do ERP.
  const awaitingTitle = reason === 'no_title'
  return <div className="grid gap-16 columns-1 padding-8 border-top" data-testid="mega-order-budget-link" data-status="unknown">
    <span className="accordion-label">{awaitingTitle ? MEGA_ORDER_AWAITING_TITLE : MEGA_BUDGET_LINK_LABELS.unknown}</span>
    {unmatched.length > 0
      ? renderRow('Códigos sem serviço na EAP', unmatched.join(', '))
      : (!reason || awaitingTitle) && <span>{MEGA_ORDER_WITHOUT_TITLE}</span>}
    <UnlinkedReasonRows reason={reason} />
  </div>
}

// Vínculo de um item de pedido MEGA: o item só tem serviço próprio quando o título identifica o
// produto dele. Sem isso, o vínculo fica no pedido (e o cartão do pedido o mostra), ou segue o
// estado do pedido.
const MegaOrderItemBudgetLink = ({ item, order, appropriations }) => {
  if (item?.budget_link_status === 'linked') {
    return <MegaBudgetLink line={item} appropriations={appropriations} />
  }
  const orderStatus = order?.budget_link_status
  if (orderStatus === 'linked') {
    return <div className="grid gap-16 columns-1 padding-8 border-top" data-testid="mega-budget-link" data-status="order">
      <span className="accordion-label">{MEGA_ITEM_LINKED_BY_ORDER}</span>
    </div>
  }
  if (orderStatus === 'unknown') return <MegaBudgetLink line={{ budget_link_status: 'unknown' }} />
  return <div className="grid gap-16 columns-1 padding-8 border-top">
    <span className="accordion-label">{MEGA_BUDGET_LINK_LABELS.unlinked}</span>
  </div>
}

// Cartão por contrato das obras MEGA (`view=contract` no back-end): as linhas de item com o
// mesmo `contract_number` chegam somadas, e os itens ficam no expandido.
export const MegaContractItem = ({ resource }) => {
  const { activeProject } = useProjectContext()
  const [open, setOpen] = useState(false)
  const title = joinText([resource?.contract_number, resource?.title]) || MISSING_VALUE
  const parent = textOrNull(resource?.parent_contract_number)
  const items = Array.isArray(resource?.items) ? resource.items : []

  return <div className="accordion-item offcanvas-accordion-item curve margin-bottom-8" data-testid="mega-contract-card">
    <div className="flex align-center margin-bottom-8">
      <button
        className={`accordion-toggle ${open ? "active" : ""}`}
        aria-label={open ? 'Recolher itens do contrato' : 'Expandir itens do contrato'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      />
      <span className="accordion-title">{title}</span>
    </div>
    <SupplierSegment name={resource?.supplier_name} code={resource?.supplier_id} />
    <div className="grid gap-16 columns-4-laptop-l margin-top-12">
      {contractValueRows(resource)}
      {renderRow('Data do contrato', formatContractDate(resource?.contract_date))}
      {parent && renderRow('Aditivo de', `Contrato ${parent}`)}
    </div>
    {open && <div className="margin-top-12">
      {items.length === 0
        ? <p className="accordion-label">Nenhum item neste contrato.</p>
        : items.map((item, idx) => (
          <div key={`${item?.resource_id ?? 'item'}-${idx}`} className="accordion-item offcanvas-accordion-item curve margin-top-8">
            <span className="accordion-title">{textOrNull(item?.description) ?? (textOrNull(item?.resource_id) ? `Produto ${textOrNull(item.resource_id)}` : MISSING_VALUE)}</span>
            <div className="grid gap-16 columns-4-laptop-l padding-8 border-top">
              {contractValueRows(item)}
            </div>
            <MegaBudgetLink line={item} appropriations={activeProject?.appropriations} />
          </div>
        ))}
    </div>}
  </div>
}

export const PurchaseOrderItem = ({ index, resource, isMega = false }) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const { getPurchaseOrders } = useProjectActions();
  // Resolvido UMA vez aqui, no topo do componente: `findWbsPath` era quem lia o
  // contexto, e como ele e' chamado de dentro do `.map()` dos itens o numero de
  // hooks do componente mudava ao expandir.
  const { activeProject: itemProject } = useProjectContext();
  const [items, setItems] = useState([])
  const orderNumber = textOrNull(resource.formattedPurchaseOrderId) ?? textOrNull(resource.purchaseOrderId)

  const getItems = async () => {
    setLoading(true)
    try {
      const response = await getPurchaseOrders('', '', '', '', resource.purchaseOrderId)
      setItems(Array.isArray(response?.res) ? response.res : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      getItems()
    }
  }, [open])


  return <><div key={index} className={`accordion-item offcanvas-accordion-item curve margin-top-8`}>
    <div>
      <div className="flex align-center margin-bottom-8">
        <button
          className={`accordion-toggle ${open ? "active" : ""}`}
          onClick={() => setOpen(!open)}
        />
        <span className="accordion-title">{orderNumber ?? MISSING_VALUE}</span>
      </div>
    </div>
    <SupplierSegment name={resource.supplier_name} code={resource.supplierId ?? resource.supplier_id} />
    <div className="grid gap-16 columns-4-laptop-l margin-top-12">
      {/* O MEGA não informa quem criou o pedido: a coluna ficaria sempre vazia. */}
      {(!isMega || textOrNull(resource.createdBy)) && renderRow(`Criado por`, resource.createdBy)}
      {renderRow('Status', getStatus[resource.status] || resource.status)}
      {renderRow(`Data`, formatDateDMY(resource?.date))}
      {renderRow('Valor', formatCurrencyOrDash(resource.value))}
      {/* Saldo do pedido (valor ainda não recebido) -- só existe nos pedidos MEGA. */}
      {hasField(resource, 'open_value') && renderRow('Saldo do pedido', formatCurrencyOrDash(resource.open_value))}
    </div>
    {isMega && <MegaOrderBudgetLink order={resource} appropriations={itemProject?.appropriations} />}
    {open && <>
      <div className="grid gap-16 columns-4 margin-top-12">
        {renderRow(`Id do Centro de Custo`, resource.costCenterId)}
        {renderRow(`Id do Fornecedor`, resource?.supplierId)}
        {renderRow(`Documento`, resource?.forecastDocumentId)}
        {renderRow(`Data de Envio`, resource?.sentDate?.slice(0, 10).split('-').reverse().join('/'))}
      </div>
      <div className="grid gap-16 columns-1 margin-top-12">
        {renderRow(`Notas internas`, resource?.internalNotes)}
      </div>
    </>
    }
  </div>
    {open && <div className="margin-left-12">
      {loading ? <></> : items.map(i => {
        const wbsLabels = purchaseOrderWbsLabels(i);
        return <div key={i._id} className="accordion-item offcanvas-accordion-item curve">
          <div>
            <div className="flex align-center">
              <span className="accordion-title" title={i.supplier_name}>{joinText([i.resource_description, i.notes])}</span>
            </div>
          </div>
          {isMega
            // A rota `PedidoCompra` não traz o estruturado: o vínculo do item MEGA vem do título,
            // e sem ele nada de "0%" apropriado nem caminho de EAP vazio.
            ? <>
              <div className="grid gap-16 columns-4-laptop-l padding-8 border-top">
                {renderRow('Quantidade', i.quantity)}
                {renderRow('Preço unitário', formatCurrencyOrDash(i.unit_price))}
                {renderRow('Valor total', formatCurrencyOrDash(i.total_price))}
                {hasField(i, 'open_value') && renderRow('Saldo do item', formatCurrencyOrDash(i.open_value))}
              </div>
              <MegaOrderItemBudgetLink item={i} order={resource} appropriations={itemProject?.appropriations} />
            </>
            : <>
              <div className="grid gap-16 columns-4-laptop-l padding-8 border-top">
                {renderRow('Quantidade', i.quantity)}
                {renderRow(`Percentual apropriado`, `${i.building_percentage_appropriation ?? i["building_percentage_appropriation]"] ?? 0}%`)}
                {renderRow('Preço unitário', formatNumberToCurrency(i.unit_price))}
                {renderRow('Valor total', formatNumberToCurrency(i.total_price))}
              </div>
              <div className="grid gap-16 columns-5-laptop-l padding-8 border-top">
                {renderRow(`Unidade - ${wbsLabels.unit}`, findWbsPath(itemProject?.appropriations, 'units', i.wbs_code_id_unit))}
                {renderRow(`Célula - ${wbsLabels.cell}`, findWbsPath(itemProject?.appropriations, 'cells', i.wbs_code_id_cell))}
                {renderRow(`Etapa - ${wbsLabels.step}`, findWbsPath(itemProject?.appropriations, 'steps', i.wbs_code_id_step))}
                {renderRow(`Subetapa - ${wbsLabels.substep}`, findWbsPath(itemProject?.appropriations, 'substeps', i.wbs_code_id_substep))}
                {renderRow(`Serviço - ${wbsLabels.service}`, findWbsPath(itemProject?.appropriations, 'services', i.wbs_code_id_service))}
              </div>
            </>}
        </div>
      })}
    </div>}
  </>
}

const isMegaErp = (erp) => String(erp || "").toUpperCase() === "MEGA";

const OrdersAndContracts = () => {
  const { getContracts, getPurchaseOrders, getAllBuildingItems, refreshPurchaseOrders } = useProjectActions();
  const { activeProject, companies, updateJobsByBuilding } = useProjectContext();
  const currentCompany = companies?.find((company) => company._id === activeProject?.company_id);
  const hasActiveProject = Boolean(activeProject?.wbs_code_id);
  const presentationMode = isPresentationProject(activeProject);
  const currentUpdateJob = updateJobsByBuilding?.[activeProject?.building_id];
  const updateInProgress = isUpdateJobActive(currentUpdateJob);
  const isMega = isMegaErp(currentCompany?.erp);
  const [dateLimit, setDateLimit] = useState({
    max: 0,
    min: 0,
  });
  const [resources, setResources] = useState([]);
  const [view, setView] = useState('purchases')
  const [search, setSearch] = useState({
    search: '',
    searchFor: '',
  })
  const [documents, setDocuments] = useState([])
  const [docFilter, setDocFilter] = useState('')
  const [isFiltering, setIsFiltering] = useState(false)
  const isFilteringRef = useRef(isFiltering);
  const [modalVisible, setModalVisible] = useState(false);
  const [datesToUpdate, setDatesToUpdate] = useState({
    min: '',
    max: ''
  })
  const [updateRes, setUpdateRes] = useState('')
  const [updateOk, setUpdateOk] = useState(true)
  const [requestingUpdate, setRequestingUpdate] = useState(false)
  // Recorte da aba de Contratos MEGA: "open" = só contratos com saldo a faturar > 0.
  const [contractBalance, setContractBalance] = useState('open')
  const [levelToGet, setLevelToGet] = useState('')
  const [levelItems, setLevelItems] = useState([])
  const [itemToFilter, setItemToFilter] = useState('')
  const [count, setCount] = useState(0)
  // Resumo por motivo do nó escolhido no filtro de nível (só vem com filtro de nível).
  const [unlinkedSummary, setUnlinkedSummary] = useState(null)
  const selectedLevelItem = levelItems.find((item) => item?.wbs_code_id === itemToFilter)
  const unappropriatedFilter = isMega && itemToFilter !== '' && isUnappropriatedOrdersContractsNode(selectedLevelItem)

  useEffect(() => {
    isFilteringRef.current = isFiltering;
  }, [isFiltering]);

  const countRef = useRef(0);

  const filtersRef = useRef({
    levelToGet: '',
    itemToFilter: ''
  });

  useEffect(() => {
    filtersRef.current = { levelToGet, itemToFilter };
  }, [levelToGet, itemToFilter]);


  useEffect(() => {
    setDatesToUpdate({ ...datesToUpdate, min: dateLimit.min })
  }, [dateLimit.min]);

  useEffect(() => {
    setDatesToUpdate({ ...datesToUpdate, max: dateLimit.max })
  }, [dateLimit.max]);

  const fetchPurchases = async () => {
    if (!hasActiveProject) return { res: [], uniqueDocs: [] };
    const { levelToGet, itemToFilter } = filtersRef.current;
    const searchFor = search.searchFor === 'supplier_name' ? 'supplier_name' : '';
    const res = await getPurchaseOrders(dateLimit, countRef.current, docFilter, search.search, "", levelToGet.slice(0, -1), itemToFilter, searchFor);
    return res ?? { res: [], uniqueDocs: [] };
  }

  const fetchContracts = async () => {
    if (!hasActiveProject) return { res: [], uniqueDocs: [] };
    const { levelToGet, itemToFilter } = filtersRef.current;
    // Obra MEGA: um cartão por contrato (`view=contract`), paginado por contrato e recortado
    // pelo saldo a faturar. Sienge segue com a lista por linha de sempre.
    const contractOptions = isMega ? { view: 'contract', balance: contractBalance } : undefined;
    const res = await getContracts('building', activeProject?.wbs_code_id, 'false', dateLimit, countRef.current, docFilter, search, levelToGet.slice(0, -1), itemToFilter, activeProject?.building_id, contractOptions);
    return res ?? { res: [], uniqueDocs: [] };
  }

  const getItens = async () => {
    if (!hasActiveProject) return;
    const response = view === "contracts"
      ? await fetchContracts()
      : await fetchPurchases();
    const nextResources = Array.isArray(response?.res) ? response.res : [];
    if (nextResources.length > 0) countRef.current += 50;

    setResources((prev) => [...prev, ...nextResources]);
    setDocuments(Array.isArray(response?.uniqueDocs) ? response.uniqueDocs : [])
    setUnlinkedSummary(response?.unlinked_summary ?? null)
  };

  const handleScroll = () => {
    if (isFilteringRef.current || search.searchFor !== "" || itemToFilter !== "") return;

    const scrolledTo = window.scrollY + window.innerHeight;
    const isReachBottom = document.body.scrollHeight === scrolledTo;

    if (isReachBottom) {
      getItens();
    }
  };

  // Só muda na aba de Contratos: o cartão por contrato depende do ERP da construtora, que pode
  // chegar depois da obra ativa.
  const megaContractsView = view === 'contracts' && isMega;

  useEffect(() => {
    countRef.current = 0;
  }, [view, dateLimit, search, itemToFilter, activeProject, contractBalance, megaContractsView])

  useEffect(() => {
    if (!hasActiveProject) {
      setResources([]);
      setDocuments([]);
      return undefined;
    }
    if (dateLimit.max !== 0 || dateLimit.min !== 0 || docFilter !== '') {
      setIsFiltering(true);
    }
    setResources(() => []);
    getItens();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeProject?.wbs_code_id, view, dateLimit, count, itemToFilter, contractBalance, megaContractsView]);

  useEffect(() => {
    setDatesToUpdate({
      min: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split("T")[0],
      max: new Date().toISOString().split("T")[0],
    })
  }, [])

  const handleWbsReset = () => {
    setIsFiltering(false);
    setLevelToGet('')
    setLevelItems([])
    setItemToFilter('')
    setUnlinkedSummary(null)
    setResources(() => []);
    countRef.current = 0;
  }

  const handleDateReset = () => {
    setIsFiltering(false);
    setDateLimit({ max: 0, min: 0 });
    setResources(() => []);
    countRef.current = 0;
  }

  const handleResetSearch = () => {
    setIsFiltering(false);
    setSearch({ search: '', searchFor: '' });
    setResources(() => []);
    countRef.current = 0;
    setCount(p => p + 1)
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setDateLimit(prev => ({ ...prev, [name]: value }))
  }

  const handleFilter = () => {
    setIsFiltering(true);
    setResources(() => []);
    countRef.current = 0;
    getItens()
  }

  const handleContractBalance = (balance) => {
    if (balance === contractBalance) return;
    setResources(() => []);
    countRef.current = 0;
    setContractBalance(balance);
  }

  const handleModalVisibility = () => {
    setModalVisible(!modalVisible);
    setUpdateRes(!modalVisible ? updateJobMessage(currentUpdateJob) : '')
    setUpdateOk(currentUpdateJob?.status !== "failed")
  };

  const getLevelItems = async () => {
    if (!hasActiveProject || !levelToGet) return;
    const res = await getAllBuildingItems([levelToGet])
    setLevelItems(Array.isArray(res?.items) ? res.items : [])
  }

  useEffect(() => {
    if (levelToGet !== '') {
      getLevelItems()
    }
  }, [levelToGet])

  const handleUpdate = async () => {
    if (requestingUpdate || updateInProgress) return;
    setRequestingUpdate(true);
    // Obra MEGA sempre recarrega o histórico inteiro de pedidos e contratos (`period: null`).
    const result = isMega
      ? await refreshPurchaseOrders(null, null)
      : await refreshPurchaseOrders(datesToUpdate.min, datesToUpdate.max);
    setUpdateOk(Boolean(result?.ok || (result?.status === 409 && result?.body?.job)))
    setUpdateRes(
      result?.body?.message
      ?? (result?.ok
        ? "Atualização solicitada. Você pode sair desta página; enviaremos um e-mail quando terminar."
        : "Não foi possível iniciar a atualização. Tente novamente."),
    )
    setRequestingUpdate(false);
  }

  const handleEditSearch = (e) => {
    const { name, value } = e.target
    if (name === 'searchFor') {
      setIsFiltering(true);
    }
    setSearch(prev => ({ ...prev, [name]: value }))
  }



  return (
    <>
      {modalVisible && !presentationMode && (
        <Modal
          title="Atualizar insumos"
          close={handleModalVisibility}
        >
          <div style={{ alignItems: "center" }} className="date-range flex column gap-8">
            {/* Obra MEGA não escolhe período: toda atualização recarrega o histórico inteiro
                de pedidos e contratos (o saldo do pedido alimenta a Tendência por composição,
                e uma janela deixaria velho o saldo de pedido antigo recebido depois dela). */}
            {isMega && (
              <p className="label">Atualiza todo o histórico de pedidos e contratos da obra.</p>
            )}
            {!isMega && (
              <>
                <div className="gap-4">
                  <span className="label margin-right-8">Data inicial</span>
                  <input
                    type="date"
                    onChange={(e) =>
                      setDatesToUpdate((prev) => ({
                        ...prev,
                        min: e.target.value,
                      }))
                    }
                    value={datesToUpdate.min}
                    name="min"
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div className="column gap-4">
                  <span className="label margin-right-8">Data final</span>
                  <input
                    type="date"
                    onChange={(e) =>
                      setDatesToUpdate((prev) => ({
                        ...prev,
                        max: e.target.value,
                      }))
                    }
                    value={datesToUpdate.max}
                    name="max"
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </>
            )}
            <button
              disabled={(!isMega && (!datesToUpdate.min || !datesToUpdate.max)) || requestingUpdate || updateInProgress}
              onClick={handleUpdate}
              className="button button-secondary small"
            >
              {requestingUpdate || updateInProgress ? "Atualizando…" : "Atualizar"}
            </button>
          </div>
          <p style={{ color: updateOk ? 'green' : 'red', fontSize: '16px', fontStyle: 'italic' }}>{updateRes}</p>
        </Modal>
      )}
      <div className="flex gap-16 graph-labels">
        <h1 className={view === "purchases" ? 'active-graph' : ''} onClick={() => setView('purchases')}>Pedidos</h1>
        <h1 className={view === "contracts" ? 'active-graph' : ''} onClick={() => setView('contracts')}>Contratos</h1>
      </div>
      <div className="flex margin-top-16 margin-bottom-4 justify-space-between gap-16 timeline-filters align-center">
        <input type="text" value={search.search} name="search" onChange={handleEditSearch} placeholder="Buscar por item" />
        {search.search !== '' &&
          <>
            {/* Pedidos buscam pelo número por padrão; "Fornecedor" envia `searchFor=supplier_name`. */}
            <select aria-label="Buscar por" name="searchFor" value={search.searchFor || (view === "purchases" ? 'contract_number' : 'dis')} onChange={handleEditSearch} >
              <option disabled value='dis'>Buscar por:</option>
              <option value='supplier_name'>Fornecedor</option>
              <option value='contract_number'>Número</option>
            </select>
            <button disabled={search.search === "" || (search.searchFor === "" && view === "contracts")} className="button button-accent small" style={{ justifySelf: 'center' }} onClick={handleFilter}>Buscar</button>
          </>
        }
        {(search.searchFor !== "" || (view === "purchases" && search.search !== "")) && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleResetSearch}>Resetar</button>}
      </div>
      <div className="flex margin-top-16 margin-bottom-4 justify-space-between gap-16 timeline-filters align-center">


        <div className="flex justify-content-center align-center gap-8">
          <span>Filtrar por:</span>
          <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
            setLevelToGet(e.target.value)
            setItemToFilter('')
          }} value={levelToGet}>
            <option value='' disabled>Selecione</option>
            <option value='units'>Unidade Construtiva</option>
            <option value='cells'>Célula</option>
            <option value='steps'>Etapa</option>
            <option value='substeps'>Subetapa</option>
            <option value='services'>Serviço</option>
          </select>
          {/* {levelItems.length > 0 && <select style={{ marginBottom: '0px', marginTop: '0px' }} onChange={(e) => {
            setItemToFilter(e.target.value)
            setIsFiltering(true)
          }
          } value={itemToFilter} defaultChecked=''>
            <option value='' disabled>Selecione</option>
            {levelItems.map(i => {
              return <option key={i.wbs_code_id} value={i.wbs_code_id}>{i.building_unit_description || i.wbs_description}</option>
            })}
          </select>} */}
          {levelItems.length > 0 && <div style={{ minWidth: '350px', width: '100%' }}>
            <Select
              isClearable={false}
              name="filteredLevel"
              options={levelItems.map(i => ({ value: i?.wbs_code_id, label: i?.building_unit_description || i?.wbs_description }))}
              onChange={(e) => {
                setItemToFilter(e.value)
                setIsFiltering(true)
              }}
              classNamePrefix="select"
              placeholder='Buscar...'
            />
          </div>}
          {<div className="flex gap-8 align-center">
            {(itemToFilter !== '' || levelToGet !== "") && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleWbsReset}>Resetar</button>}
          </div>}
        </div>

        <div className="date-range">
          <span className="label">Início</span>
          <input type="date" onChange={handleDateChange} value={dateLimit.min} name="min" max={new Date().toISOString().split('T')[0]} />
          <span className="label">Fim</span>
          <input type="date" onChange={handleDateChange} value={dateLimit.max} name="max" max={new Date().toISOString().split('T')[0]} />
        </div>
        {<div className="flex gap-8 align-center">
          {(dateLimit.min !== 0 || dateLimit.max !== 0) && <button className="button button-error small" style={{ justifySelf: 'center' }} onClick={handleDateReset}>Resetar</button>}
        </div>}
        {view === "purchases" && !presentationMode && <button className="button button-secondary small" onClick={handleModalVisibility} title="Atualizar itens dos pedidos">Atualizar</button>}
      </div>

      {/* Contratos MEGA: por padrão só os que ainda têm saldo a faturar (o que já virou NF
          está no incorrido); "Todos" traz também os quitados. */}
      {megaContractsView && (
        <div className="flex gap-8 align-center margin-top-16 margin-bottom-4" role="group" aria-label="Recorte de contratos">
          <button
            type="button"
            aria-pressed={contractBalance === 'open'}
            className={`button small ${contractBalance === 'open' ? 'button-accent' : 'button-secondary'}`}
            onClick={() => handleContractBalance('open')}
          >
            Com saldo
          </button>
          <button
            type="button"
            aria-pressed={contractBalance === 'all'}
            className={`button small ${contractBalance === 'all' ? 'button-accent' : 'button-secondary'}`}
            onClick={() => handleContractBalance('all')}
          >
            Todos
          </button>
        </div>
      )}

      {/* Contratos: os títulos anulados na Ampli saem do pago do credor. O resumo da obra não
          liga o título ao contrato, então o ícone fica no topo da lista e vale para todos. */}
      {view === "contracts" && (
        <AnnulmentIndicator summary={activeProject?.titleAnnulments} kind="paid" placement="row" />
      )}

      {unappropriatedFilter && unlinkedSummary && (
        <UnlinkedSummary summary={unlinkedSummary} origin={view === 'contracts' ? 'contracts' : 'purchases'} />
      )}

      {resources?.map((i, idx) => {
        if (view !== "contracts") return <PurchaseOrderItem key={i._id} index={i._id} resource={i} isMega={isMega} />
        if (megaContractsView) return <MegaContractItem key={i.contract_number ?? i._id ?? idx} resource={i} />
        return <Item key={i._id} index={i._id} resource={i} paid={view === "paid"} />
      })}
    </>
  );
};

export default OrdersAndContracts;
